<template>
  <div class="points-audit-container">
    <h1 class="page-title">积分审核</h1>
    
    <el-card class="content-card">
      <div class="card-header">
        <div class="search-area">
          <el-select 
            v-model="searchForm.status" 
            placeholder="选择状态"
            style="width: 150px; margin-right: 10px;"
            clearable
          >
            <el-option label="全部" value="" />
            <el-option label="待审核" value="pending" />
            <el-option label="已通过" value="approved" />
            <el-option label="已拒绝" value="rejected" />
          </el-select>
          <el-select 
            v-model="searchForm.channel_id" 
            placeholder="选择渠道"
            style="width: 150px; margin-right: 10px;"
            clearable
          >
            <el-option label="全部" value="" />
            <el-option 
              v-for="channel in consumptionChannels" 
              :key="channel.id" 
              :label="channel.name" 
              :value="channel.id" 
            />
          </el-select>
          <el-input
            v-model="searchForm.phone"
            placeholder="手机号"
            style="width: 200px; margin-right: 10px;"
            clearable
          />
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            style="width: 300px; margin-right: 10px;"
            clearable
          />
          <el-button type="primary" @click="handleSearch" icon="Search">搜索</el-button>
        </div>
      </div>
      
      <div class="card-content">
        <el-table v-loading="tableLoading" :data="auditList" border stripe style="width: 100%">

          <el-table-column label="用户信息" width="280">
            <template #default="scope">
              <div class="user-info">
                <div class="nickname">{{ scope.row.user?.nickname || scope.row.user?.name || '-' }}</div>
                <div class="phone">{{ scope.row.user?.phone || '-' }}</div>
                <div class="member-level">
                  <el-tag size="small">{{ getMemberLevel(scope.row.user?.member_level) }}</el-tag>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="渠道信息" width="120">
            <template #default="scope">
               {{ scope.row.channel_name }}
            </template>
          </el-table-column>

          <el-table-column label="支付方式" width="150">
            <template #default="scope">
              <span>{{ scope.row.payment_method?.name || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="consumption_amount" label="消费金额" width="120" align="right">
            <template #default="scope">
              <span class="amount">¥{{ scope.row.consumption_amount || '0' }}</span>
            </template>
          </el-table-column>

          <el-table-column prop="order_no" label="单号信息" width="180">
            <template #default="scope">
              <span class="order-no">{{ scope.row.order_no || '-' }}</span>
            </template>
          </el-table-column>

          <el-table-column label="状态" width="100">
            <template #default="scope">
              <el-tag :type="getStatusTagType(scope.row.audit_status)">
                {{ getStatusText(scope.row.audit_status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="150" fixed="right">
            <template #default="scope">
              <template v-if="scope.row.audit_status === 'pending'">
                <el-button 
                  type="primary" 
                  size="small" 
                  @click="handleAuditDialog(scope.row)"
                >
                  审核
                </el-button>
              </template>
              <template v-else>
                <el-button 
                  type="info" 
                  size="small" 
                  @click="handleAuditDialog(scope.row)"
                >
                  查看
                </el-button>
              </template>
            </template>
          </el-table-column>
        </el-table>
        
        <div class="pagination-container">
          <el-pagination
            :current-page="pagination.currentPage"
            :page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pagination.total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </el-card>
    
    <el-dialog
      v-model="auditDialogVisible"
      :title="currentAudit?.audit_status === 'pending' ? '审核申请' : '查看审核详情'"
      width="700px"
    >
      <div v-if="currentAudit" class="audit-dialog-content">
        <!-- 基础信息展示 -->
        <h3 class="audit-dialog-title">基础信息</h3>
        <el-descriptions :column="2" border>
          <el-descriptions-item label="提交时间">{{ formatDateTime(currentAudit.created_at) }}</el-descriptions-item>
          <el-descriptions-item label="用户昵称">{{ currentAudit.user?.nickname || '-' }}</el-descriptions-item>
          <el-descriptions-item label="手机号码">{{ currentAudit.user?.phone || '-' }}</el-descriptions-item>
          <el-descriptions-item label="会员等级">{{ getMemberLevel(currentAudit.user?.member_level) }}</el-descriptions-item>
          <el-descriptions-item label="渠道类型">{{ getChannelText(currentAudit.channel_type) }}</el-descriptions-item>
          <el-descriptions-item label="店铺名称">{{ currentAudit.channel?.name || currentAudit.channel_name || '-' }}</el-descriptions-item>
          <el-descriptions-item label="渠道">{{ currentAudit.channel?.name || '-' }}</el-descriptions-item>
          <el-descriptions-item label="支付方式">{{ currentAudit.payment_method?.name || '-' }}</el-descriptions-item>
          <el-descriptions-item label="消费金额" span="1">¥{{ currentAudit.consumption_amount || '0' }}</el-descriptions-item>
          <el-descriptions-item label="单号信息">{{ currentAudit.order_no || '-' }}</el-descriptions-item>
        </el-descriptions>
        
        <!-- 交易凭证图片 -->
        <div class="transaction-images-section">
          <h4>交易凭证图片</h4>
          <div class="transaction-images">
            <el-image 
              v-for="(image, index) in (Array.isArray(currentAudit.transaction_images) ? currentAudit.transaction_images : (typeof currentAudit.transaction_images === 'string' ? currentAudit.transaction_images.split(',') : []))" 
              :key="index"
              :src="image"
              :preview-src-list="Array.isArray(currentAudit.transaction_images) ? currentAudit.transaction_images : (typeof currentAudit.transaction_images === 'string' ? currentAudit.transaction_images.split(',') : [])"
              fit="cover"
              class="audit-transaction-image"
              preview-teleported
            />
            <span v-if="!currentAudit.transaction_images || (Array.isArray(currentAudit.transaction_images) && currentAudit.transaction_images.length === 0)">无图片</span>
          </div>
        </div>
        
        <!-- 审核结果信息（仅已审核的申请显示） -->
        <template v-if="currentAudit.audit_status !== 'pending'">
          <h3 class="audit-dialog-title">审核结果</h3>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="审核状态">
              <el-tag :type="getStatusTagType(currentAudit.audit_status)">
                {{ getStatusText(currentAudit.audit_status) }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="审核时间">{{ currentAudit.audited_at ? formatDateTime(currentAudit.audited_at) : '-' }}</el-descriptions-item>
            <el-descriptions-item label="拒绝原因" span="2">{{ currentAudit.audit_remark || '-' }}</el-descriptions-item>
            <el-descriptions-item label="是否允许重新申请">
              <el-tag :type="currentAudit.allow_reapplication ? 'success' : 'danger'">
                {{ currentAudit.allow_reapplication ? '允许' : '不允许' }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="发放积分">{{ currentAudit.points_amount || 0 }}</el-descriptions-item>
          </el-descriptions>
        </template>
        
        <!-- 审核操作（仅待审核的申请显示） -->
        <div v-if="currentAudit.audit_status === 'pending'" class="audit-actions">
          <h4>审核操作</h4>
          <el-radio-group v-model="auditForm.audit_status" class="audit-status-radio">
            <el-radio label="approved">通过</el-radio>
            <el-radio label="rejected">拒绝</el-radio>
          </el-radio-group>
          
          <!-- 拒绝原因输入框 -->
          <el-form :model="auditForm" ref="auditFormRef" label-width="100px" class="audit-form">
            <el-form-item 
              label="拒绝原因" 
              prop="audit_remark" 
              :rules="{ required: auditForm.audit_status === 'rejected', message: '请输入拒绝原因', trigger: 'blur' }"
              v-if="auditForm.audit_status === 'rejected'"
            >
              <el-input 
                v-model="auditForm.audit_remark" 
                type="textarea" 
                :rows="3"
                placeholder="请输入拒绝原因" 
              />
            </el-form-item>
            
            <!-- 是否允许重新申请 -->
            <el-form-item 
              label="是否允许重新申请" 
              prop="allow_reapplication" 
              :rules="{ required: auditForm.audit_status === 'rejected', message: '请选择是否允许重新申请', trigger: 'change' }"
              v-if="auditForm.audit_status === 'rejected'"
            >
              <el-radio-group v-model="auditForm.allow_reapplication">
                <el-radio :label="true">允许</el-radio>
                <el-radio :label="false">不允许</el-radio>
              </el-radio-group>
            </el-form-item>
            
            <!-- 积分数量输入框 -->
            <el-form-item 
              label="积分数量" 
              prop="points_amount" 
              :rules="{ required: auditForm.audit_status === 'approved', message: '积分数量不能为空', trigger: 'blur', type: 'number', min: 1 }"
              v-if="auditForm.audit_status === 'approved'"
            >
              <el-input-number 
                v-model="auditForm.points_amount" 
                :min="1" 
                :precision="0"
                placeholder="系统自动计算积分数量" 
                disabled
              />
              <div class="points-calculation-hint">
                <small>积分计算规则：消费金额 × 积分比例（当前比例：{{ Math.round((pointSettings.system_consumption_point_ratio || 1.0) * 100) }}%）</small>
              </div>
            </el-form-item>
          </el-form>
        </div>
      </div>
      
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="handleCancelAudit">关闭</el-button>
          <el-button 
            v-if="currentAudit?.audit_status === 'pending'" 
            type="primary" 
            @click="submitAudit" 
            :loading="auditLoading"
          >
            确认审核
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive, watch } from 'vue';
import { ElMessage } from 'element-plus';
import request from '../../api/axios';
import * as pointApi from '../../api/point';
import * as consumptionChannelApi from '../../api/consumptionChannel';

const auditList = ref([]);
const tableLoading = ref(false);
const searchForm = reactive({
  status: '',
  channel_id: '',
  phone: '',
  start_date: '',
  end_date: ''
});
const dateRange = ref([]);
const pagination = reactive({
  currentPage: 1,
  pageSize: 10,
  total: 0
});
const auditDialogVisible = ref(false);
const auditFormRef = ref();
const currentAudit = ref(null);
const auditForm = reactive({
  audit_status: 'approved',
  audit_remark: '',
  allow_reapplication: true,
  points_amount: 1
});
const auditLoading = ref(false);

// 积分设置数据
const pointSettings = ref({
  system_consumption_point_ratio: 1.0 // 默认100%比例
});

// 消费渠道列表
const consumptionChannels = ref([]);

// 获取积分设置
async function fetchPointSettings() {
  try {
    const response = await pointApi.getPointSettings();
    console.log('积分设置响应:', response);
    if (response.code === 200 && response.data) {
      // 合并响应数据与默认值，确保始终有system_consumption_point_ratio属性
      pointSettings.value = {
        system_consumption_point_ratio: 1.0,
        ...response.data
      };
      console.log('更新积分设置:', pointSettings.value);
    } else {
      // 响应格式不正确时使用默认值
      pointSettings.value = {
        system_consumption_point_ratio: 1.0
      };
    }
  } catch (error) {
    console.error('获取积分设置失败:', error);
    // 失败时使用默认值
    pointSettings.value = {
      system_consumption_point_ratio: 1.0
    };
  }
}

// 获取消费渠道列表
async function fetchConsumptionChannels() {
  try {
    const response = await consumptionChannelApi.getFrontendConsumptionChannels();
    console.log('消费渠道响应:', response);
    if (response.code === 200 && response.data) {
      consumptionChannels.value = response.data;
      console.log('更新消费渠道:', consumptionChannels.value);
    } else {
      consumptionChannels.value = [];
    }
  } catch (error) {
    console.error('获取消费渠道失败:', error);
    consumptionChannels.value = [];
  }
}

// 计算积分数量
function calculatePoints(amount) {
  if (!amount || typeof amount !== 'number') return 0;
  // 消费金额 * 积分比例 = 积分数量
  const ratio = pointSettings?.value?.system_consumption_point_ratio || 1.0;
  const points = Math.floor(amount * ratio);
  return Math.max(1, points); // 至少1积分
}

watch(
  () => dateRange.value,
  (newVal) => {
    if (newVal && newVal.length === 2) {
      searchForm.start_date = newVal[0];
      searchForm.end_date = newVal[1];
    } else {
      searchForm.start_date = '';
      searchForm.end_date = '';
    }
  }
);

const fetchAuditList = async () => {
  try {
    tableLoading.value = true;
    const response = await request({
      url: '/manage/points/audit/list',
      method: 'get',
      params: {
        status: searchForm.status,
        channel_id: searchForm.channel_id,
        phone: searchForm.phone,
        start_date: searchForm.start_date,
        end_date: searchForm.end_date,
        page: pagination.currentPage,
        pageSize: pagination.pageSize
      }
    });
    // 直接使用response.data和response.total，因为axios拦截器已经处理了响应
    auditList.value = response.data || [];
    pagination.total = response.total || 0;
  } catch (error) {
    ElMessage.error('获取审核列表失败');
    console.error('获取审核列表失败:', error);
  } finally {
    tableLoading.value = false;
  }
};

onMounted(() => {
  fetchAuditList();
  fetchPointSettings();
  fetchConsumptionChannels();
});

const handleSearch = () => {
  pagination.currentPage = 1;
  fetchAuditList();
};

const handleSizeChange = (size) => {
  pagination.pageSize = size;
  fetchAuditList();
};

const handleCurrentChange = (current) => {
  pagination.currentPage = current;
  fetchAuditList();
};

const handleAuditDialog = async (row) => {
  currentAudit.value = row;
  // 重置表单
  auditForm.audit_status = 'approved';
  auditForm.audit_remark = '';
  // 确保获取最新的积分设置
  await fetchPointSettings();
  // 自动计算积分数量
  auditForm.points_amount = calculatePoints(parseFloat(row.consumption_amount) || 0);
  auditDialogVisible.value = true;
};

const handleCancelAudit = () => {
  auditDialogVisible.value = false;
  currentAudit.value = null;
};

const submitAudit = async () => {
  if (!auditFormRef.value || !currentAudit.value) return;
  
  try {
    await auditFormRef.value.validate();
    
    auditLoading.value = true;
    
    await request({
      url: `/manage/points/audit/${currentAudit.value.id}/audit`,
      method: 'put',
      data: {
        audit_status: auditForm.audit_status,
        audit_remark: auditForm.audit_remark,
        allow_reapplication: auditForm.audit_status === 'rejected' ? auditForm.allow_reapplication : undefined,
        points_amount: auditForm.audit_status === 'approved' ? auditForm.points_amount : undefined
      }
    });
    
    ElMessage.success(`审核${auditForm.audit_status === 'approved' ? '通过' : '拒绝'}成功`);
    auditDialogVisible.value = false;
    currentAudit.value = null;
    fetchAuditList();
  } catch (error) {
    if (error.name === 'Error') {
      return;
    }
    ElMessage.error('审核失败');
    console.error('审核失败:', error);
  } finally {
    auditLoading.value = false;
  }
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toISOString().slice(0, 19).replace('T', ' ');
};

const getMemberLevel = (level) => {
  const levels = {
    1: '普通会员',
    2: '银卡会员',
    3: '金卡会员',
    4: '铂金会员',
    5: '钻石会员'
  };
  return levels[level] || '普通会员';
};

const getChannelTypeTag = (type) => {
  const typeMap = {
    'offline': 'primary',
    'taobao': 'warning'
  };
  return typeMap[type] || 'info';
};

const getChannelText = (type) => {
  // 从consumptionChannels中获取渠道名称
  const channel = consumptionChannels.value.find(ch => ch.code === type);
  if (channel) {
    return channel.name;
  }
  // 回退到默认处理
  return type || '-';
};



const getStatusTagType = (status) => {
  const statusMap = {
    'pending': 'warning',
    'approved': 'success',
    'rejected': 'danger'
  };
  return statusMap[status] || 'info';
};

const getStatusText = (status) => {
  const statusMap = {
    'pending': '待审核',
    'approved': '已通过',
    'rejected': '已拒绝'
  };
  return statusMap[status] || status;
};
</script>

<style scoped>
.points-audit-container {
  padding: 20px;
}

.page-title {
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
}

.content-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 10px;
}

.search-area {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
}

.card-content {
  padding: 20px 0;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.user-info .nickname {
  font-weight: 500;
  color: #333;
}

.user-info .phone {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}

.user-info .member-level {
  margin-top: 4px;
}

.user-info .member-level .el-tag {
  margin-right: 0;
}

.amount {
  color: #F56C6C;
  font-weight: 500;
}

.order-no {
  font-size: 12px;
  color: #606266;
  word-break: break-all;
}

.transaction-image {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  cursor: pointer;
}

.no-action {
  color: #C0C4CC;
}

/* 审核对话框样式 */
.audit-dialog-content {
  padding: 10px 0;
}

.audit-dialog-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #333;
}

.transaction-images-section {
  margin: 20px 0;
}

.transaction-images-section h4 {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
  color: #333;
}

.transaction-images {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.audit-transaction-image {
  width: 80px;
  height: 80px;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid #e4e7ed;
}

.audit-actions {
  margin: 20px 0;
}

.audit-actions h4 {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #333;
}

.audit-status-radio {
  margin-bottom: 20px;
  display: flex;
  gap: 20px;
}

.audit-form {
  margin-top: 20px;
}

/* 积分计算提示样式 */
.points-calculation-hint {
  margin-top: 8px;
  color: #909399;
  font-size: 12px;
  line-height: 1.4;
}
</style>
